/**
 * Per-user Platform Report favourites.
 *
 * One provider scoped to the Platform Reports route subtree (see `Routes.tsx`)
 * so the hub and every report view share a single favourites source. Server is
 * the source of truth (never localStorage); `toggle` updates optimistically and
 * rolls back on failure with an error toast.
 *
 * Favourite ids are hub-row ids: `REPORT_TYPE` for operational reports,
 * `exception-<key>` for exception rows.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { platformReportsService } from '../../../services/platformReportsService'
import { useNotify } from '../../../components/shared/ToastProvider'

interface FavouritesContextValue {
  favourites: Set<string>
  isFavourite: (id: string) => boolean
  toggle: (id: string) => void
  loading: boolean
}

const FavouritesContext = createContext<FavouritesContextValue | null>(null)

export function PlatformReportFavouritesProvider({ children }: { children: React.ReactNode }) {
  const { showError } = useNotify()
  const [favourites, setFavourites] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)

  // Seed once on mount so a direct navigation to a report view (which does not
  // otherwise list report types) still knows the star's initial state.
  useEffect(() => {
    let active = true
    platformReportsService
      .listReportTypes()
      .then((payload) => {
        if (active) setFavourites(new Set(payload.favourites))
      })
      .catch(() => {
        /* a missing favourites seed degrades to empty stars; non-critical */
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const isFavourite = useCallback((id: string) => favourites.has(id), [favourites])

  const toggle = useCallback(
    (id: string) => {
      const wasFavourite = favourites.has(id)
      // Optimistic flip.
      setFavourites((prev) => {
        const next = new Set(prev)
        if (wasFavourite) next.delete(id)
        else next.add(id)
        return next
      })
      const request = wasFavourite
        ? platformReportsService.removeFavourite(id)
        : platformReportsService.addFavourite(id)
      request
        .then((serverFavourites) => {
          // Reconcile with the server's authoritative list.
          setFavourites(new Set(serverFavourites))
        })
        .catch(() => {
          // Roll back to the pre-toggle state.
          setFavourites((prev) => {
            const next = new Set(prev)
            if (wasFavourite) next.add(id)
            else next.delete(id)
            return next
          })
          showError(
            wasFavourite
              ? 'Could not remove from favourites. Please try again.'
              : 'Could not add to favourites. Please try again.',
          )
        })
    },
    [favourites, showError],
  )

  return (
    <FavouritesContext.Provider value={{ favourites, isFavourite, toggle, loading }}>
      {children}
    </FavouritesContext.Provider>
  )
}

/**
 * Access the favourites context. Returns a no-op fallback when no provider is
 * present (e.g. a component rendered in isolation by a test harness).
 */
export function usePlatformReportFavourites(): FavouritesContextValue {
  const ctx = useContext(FavouritesContext)
  if (ctx) return ctx
  return {
    favourites: new Set(),
    isFavourite: () => false,
    toggle: () => {},
    loading: false,
  }
}
